"use client";

import React from "react";
import { cn } from "@/lib/utils";
import {
  accentColorConfig,
  trendConfig,
  badgeVariantConfig,
  type AccentColor,
  type CKpiCardTrend,
  type CKpiCardBadge,
} from "./config";

/**
 * CKpiCard - Reusable KPI Stat Card Component
 *
 * A flexible stat card with optional icon, value, badge, trend indicator,
 * and footer. Designed for data display across all pages.
 *
 * @example
 * // Basic usage
 * <CKpiCard
 *   label="Total Tasks"
 *   value="156"
 *   accentColor="indigo"
 * />
 *
 * @example
 * // Full featured
 * <CKpiCard
 *   label="Hours This Week"
 *   value="24.5"
 *   unit="hrs"
 *   footer="Target: 40 hrs"
 *   accentColor="indigo"
 *   icon={<ClockIcon className="w-4 h-4" />}
 *   trend={{ direction: "up", value: "12%" }}
 * />
 *
 * @example
 * // With badge
 * <CKpiCard
 *   label="My Points"
 *   value="1,250"
 *   badge={{ text: "+85 this week", variant: "success" }}
 *   footer="Level 5 - Silver Server"
 *   accentColor="violet"
 *   icon={<StarIcon className="w-4 h-4" />}
 * />
 */

// Re-export types from config
export type {
  AccentColor,
  CKpiCardTrend,
  CKpiCardBadge,
  BadgeVariant,
} from "./config";

export interface CKpiCardProps {
  /** Card label/title displayed at the top */
  label: string;
  /** Main value to display (e.g., "24.5", "1,250", "£1,850") */
  value: string;
  /** Optional unit shown after value (e.g., "hrs", "est.") */
  unit?: string;
  /** Optional badge shown next to value */
  badge?: CKpiCardBadge;
  /** Optional trend indicator */
  trend?: CKpiCardTrend;
  /** Optional footer text displayed at bottom */
  footer?: string;
  /** Accent color for the icon (default: "indigo") */
  accentColor?: AccentColor;
  /** Optional icon to display (should be sized w-4 h-4) */
  icon?: React.ReactNode;
  /** Loading state - shows skeleton */
  loading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

const CKpiCard: React.FC<CKpiCardProps> = ({
  label,
  value,
  unit,
  badge,
  trend,
  footer,
  accentColor = "indigo",
  icon,
  className,
}) => {
  const colorConfig = accentColorConfig[accentColor];
  const TrendIcon = trend ? trendConfig[trend.direction].icon : null;

  return (
    <div
      className={cn(
        "rounded-md p-4",
        "bg-card/30",
        "border border-border/50",
        "text-card-foreground",
        "backdrop-blur-lg",
        className,
      )}
      style={{ backdropFilter: "blur(20px)" }}
    >
      {/* Header: Label + Icon */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        {icon && (
          <div
            className={cn(
              "w-8 h-8 rounded-md bg-linear-to-br flex items-center justify-center",
              colorConfig.iconGradient,
            )}
          >
            <span className={colorConfig.iconText}>{icon}</span>
          </div>
        )}
      </div>

      {/* Value row: Value + Unit or Badge or Trend */}
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-bold text-foreground">{value}</span>
        {unit && (
          <span className="text-xs font-medium text-muted-foreground">
            {unit}
          </span>
        )}
        {badge && (
          <span
            className={cn(
              "text-xs font-semibold px-1.5 py-0.5 rounded-full",
              badgeVariantConfig[badge.variant],
            )}
          >
            {badge.text}
          </span>
        )}
        {trend && TrendIcon && (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 text-xs font-medium",
              trendConfig[trend.direction].color,
            )}
          >
            <TrendIcon className="w-3 h-3" />
            {trend.value}
          </span>
        )}
      </div>

      {/* Footer (optional) */}
      {footer && (
        <div className="text-[11px] text-muted-foreground mt-1">{footer}</div>
      )}
    </div>
  );
};

export default CKpiCard;
